// src/hooks/useServerPermissions.js
//
// The login token carries role permissions only. Company-level permission
// overrides (tenant_permission_overrides) are resolved by the server at
// GET /api/authz/effective-permissions. Screens marked `serverResolved` use
// this hook so a granted override shows the page; the server still checks
// every call, so this is display only and never widens what the server allows.
//
// Fetched once per access token and cached in memory. Any error falls back to
// the token permissions (fail closed: never shows more than the token or the
// server allows).

import { useEffect, useState } from 'react';
import ApiCall from '../utils/apicall';
import { getAccessToken } from '../utils/authToken';

let cache = { token: null, promise: null, permissions: null };

export function mergePermissions(tokenPermissions, serverPermissions) {
  const merged = new Set(Array.isArray(tokenPermissions) ? tokenPermissions : []);
  if (Array.isArray(serverPermissions)) serverPermissions.forEach((p) => merged.add(p));
  return [...merged];
}

function fetchServerPermissions(token) {
  if (cache.token === token && cache.promise) return cache.promise;
  const promise = ApiCall.get('/authz/effective-permissions')
    .then((res) => (Array.isArray(res?.data?.permissions) ? res.data.permissions : []))
    .catch(() => []);
  cache = { token, promise, permissions: null };
  promise.then((p) => { if (cache.token === token) cache.permissions = p; });
  return promise;
}

/**
 * @param {boolean} enabled  fetch only when a serverResolved check needs it
 * @returns {{ permissions: string[] | null, loading: boolean }}
 */
export default function useServerPermissions(enabled) {
  const token = enabled ? getAccessToken() : null;
  const cached = token && cache.token === token ? cache.permissions : null;
  const [permissions, setPermissions] = useState(cached);
  useEffect(() => {
    if (!token) { setPermissions(null); return undefined; }
    // Token changed while mounted: drop the previous token's set until this one resolves.
    setPermissions(cache.token === token ? cache.permissions : null);
    let live = true;
    fetchServerPermissions(token).then((p) => { if (live) setPermissions(p); });
    return () => { live = false; };
  }, [token]);
  return { permissions, loading: Boolean(token) && permissions === null };
}

export function resetServerPermissionsCache() { cache = { token: null, promise: null, permissions: null }; }
